import { createDataTreeCache, createDataCache, validators } from 'lib/cache'

import { transformQuarterResponse } from './helper'

import api from 'stores/api'

const conditionalsTopicData = [
  { name: 'gacs' },
  {
    name: 'time',
    argToKeys: (arg, args) => {
      const isQuarter = args[3]
      const keys = []
      for (let i = arg.from; i <= arg.to; i++) {
        if (!isQuarter) {
          keys.push(i)
        } else {
          for (let quarter = 1; quarter <= 4; quarter++) {
            keys.push(i + '-' + quarter)
          }
        }
      }
      return keys
    },
    keysToArg: (keys, args) => {
      const requiredTime = args[1]?.requiredTime || null
      const isQuarter = args[3]
      if (isQuarter) {
        keys = keys.reduce((keys, key) => {
          if (key.indexOf('-1') >= 0) {
            keys.push(key.replace('-1', ''))
          }
          return keys
        }, [])
      }
      const time = {
        from: Math.min(...keys),
        to: Math.max(...keys),
      }
      if (requiredTime !== null) {
        time.requiredTime = requiredTime
      }
      return time
    },
    validators: [
      validators.isType('object'),
      (val) => validators.isType('number')(val.from),
      (val) => validators.isType('number')(val.to),
    ],
  },
  { name: 'dataSources' },
  {
    name: 'isQuarter',
    ignoreOnCaching: true,
    validators: [validators.isType('boolean')],
  },
]

const getterTopicData = (gacs, time, dataSources, isQuarter) => {
  const params = {
    format: 'tree',
    failOnAuthz: false,
    dataSources,
    gacs,
    time,
  }

  return api.Data.getData(params).then((result) => {
    if (!(result.data.status && result.data.status === 'success')) {
      throw new Error(result.data.messages)
    }
    return isQuarter ? transformQuarterResponse(result.data.data) : result.data.data
  })
}

export const [getTopicData, bustTopicData] = createDataTreeCache(conditionalsTopicData, getterTopicData, {
  name: 'MarketDataCacheTopicData',
  logPerformance: false,
  maxSize: 25000,
})

const conditionalsTopicReferenceData = [
  {
    name: 'referenceCurve',
    validators: [(val) => val.match(/^[A-D]$/)],
    argToKeys: (arg) => [arg],
    keysToArg: (keys) => keys[0],
  },
  {
    name: 'time',
    argToKeys: (arg, args) => {
      const isQuarter = args[3]
      const keys = []
      for (let i = arg.from; i <= arg.to; i++) {
        if (!isQuarter) {
          keys.push(i)
        } else {
          for (let quarter = 1; quarter <= 4; quarter++) {
            keys.push(i + '-' + quarter)
          }
        }
      }
      return keys
    },
    keysToArg: (keys, args) => {
      const isQuarter = args[3]
      if (isQuarter) {
        keys = keys.reduce((keys, key) => {
          if (key.indexOf('-1') >= 0) {
            keys.push(key.replace('-1', ''))
          }
          return keys
        }, [])
      }
      return {
        from: Math.min(...keys),
        to: Math.max(...keys),
      }
    },
    validators: [
      validators.isType('object'),
      (val) => validators.isType('number')(val.from),
      (val) => validators.isType('number')(val.to),
    ],
  },
  { name: 'dataSources' },
  {
    name: 'isQuarter',
    ignoreOnCaching: true,
    validators: [validators.isType('boolean')],
  },
]

const getterTopicReferenceData = (referenceCurve, time, dataSources, isQuarter) => {
  const params = {
    format: 'tree',
    failOnAuthz: false,
    cityType: referenceCurve,
    dataSources,
    time,
  }

  return api.Data.getDataAverageByCityType(params).then((result) => {
    if (!(result.data.status && result.data.status === 'success')) {
      throw new Error(result.data.messages)
    }
    return isQuarter ? transformQuarterResponse(result.data.data) : result.data.data
  })
}

export const [getTopicReferenceData, bustTopicReferenceData] = createDataTreeCache(
  conditionalsTopicReferenceData,
  getterTopicReferenceData,
  {
    name: 'MarketDataCacheTopicReferenceData',
    maxSize: 25000,
  }
)

const getterMarketDataTopics = () => {
  return api.Config.topics(true).then((result) => {
    if (!(result.data.status && result.data.status === 'success')) {
      throw new Error(result.data.messages)
    }
    return result.data.data
  })
}

export const [getMarketDataTopics, bustMarketDataTopics] = createDataCache([], getterMarketDataTopics, {
  name: 'MarketDataTopics',
})

const getterSubmarketEquivalents = () => {
  return api.Config.byType('submarketEquivalents').then((result) => {
    if (!(result.data.status && result.data.status === 'success')) {
      throw new Error(result.data.messages)
    }
    return result.data.data
  })
}

export const [getSubmarketEquivalents, bustSubmarketEquivalents] = createDataCache(
  [],
  getterSubmarketEquivalents,
  {
    name: 'SubmarketEquivalents',
  }
)

const getterSubmarketChartAlternative = () => {
  return api.Config.byType('submarketChartAlternative').then((result) => {
    if (!(result.data.status && result.data.status === 'success')) {
      throw new Error(result.data.messages)
    }
    return result.data.data
  })
}

export const [getSubmarketChartAlternative, bustSubmarketChartAlternative] = createDataCache(
  [],
  getterSubmarketChartAlternative,
  {
    name: 'SubmarketChartAlternative',
  }
)

const getterAvailableTopicDefinitions = () => {
  return api.TopicDefinitions.getAvailableTopicDefinitions().then((result) => {
    if (!(result.data.status && result.data.status === 'success')) {
      throw new Error(result.data.messages)
    }
    return result.data.data
  })
}

export const [getAvailableTopicDefinitions, bustAvailableTopicDefinitions] = createDataCache(
  [],
  getterAvailableTopicDefinitions,
  {
    name: 'AvailableTopicDefinitions',
  }
)

const conditionalsScores = [{ name: 'assetClass' }]

const getterScores = (assetClass) => {
  return api.Scoring.getScores(assetClass).then((result) => {
    if (!(result.data.status && result.data.status === 'success')) {
      throw new Error(result.data.messages)
    }
    return result.data.data
  })
}

export const [getScores, bustScores] = createDataCache(conditionalsScores, getterScores, {
  name: 'MarketDataScoringCacheScores',
})

const getterDetail = (gac) => {
  return api.Scoring.getDetail(gac).then((result) => {
    if (!(result.data.status && result.data.status === 'success')) {
      throw new Error(result.data.messages)
    }
    return result.data.data
  })
}

const conditionalsDetail = [{ name: 'gac' }]

export const [getDetail, bustDetail] = createDataCache(conditionalsDetail, getterDetail, {
  name: 'MarketDataScoringCacheDetail',
})

const conditionalsTopicDefinition = []

const getterScoringTopicData = () => {
  return api.Scoring.getScoringDataSources().then((result) => {
    if (!(result.data.status && result.data.status === 'success')) {
      throw new Error(result.data.messages)
    }
    return result.data.data
  })
}

export const [getScoringTopicData, bustScoringTopicData] = createDataCache(
  conditionalsTopicDefinition,
  getterScoringTopicData,
  {
    name: 'MarketDataTopicCacheScoringData',
    logPerformance: false,
    maxSize: 25000,
  }
)
